import { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { StaticContext } from 'react-router';
import Hotkeys from 'react-hot-keys';
import { getPrescriptionById, getItemFromMisc, MISC_KEYS } from '../../store/idbHandler';
import {
    clearPrescriptionPad,
    loadPrescription,
    setToastType,
    undo,
    upsertPrescriptionV2,
} from '../../store/actions/prescriptionPad';
import { PPRouterProps } from '../../routes/paths';
import { GlobalState } from '../../store/reducers';
import { Helmet } from 'react-helmet';
import {
    isReferalDialogVisible,
    isSummaryDialogVisible,
    getIsPastVitalsDialogVisible,
    getPatient,
} from '../../store/selectors/prescriptionPad';
import FinishedPanel from '../../components/prescriptionPad/rightPanel/finishScreen/FinishedPanel';
import { PrintConfig, Translate } from '../../api/pushVisit';
import { AnimatePresence } from 'framer-motion';
import { PX_TIME, track, timeTrackStart } from '../../tracking';
import { EditPatientWrapperDialog } from '../../components/common/EditPatientDialogWrapper';
import DraftToast from '../../components/prescriptionPad/rightPanel/workspace/DraftToast';
import PushSuccessToast from '../../components/prescriptionPad/rightPanel/workspace/PushSuccessToast';
import SummaryView from '../../components/prescriptionPad/SummaryView';
import { Actions } from '../../store/actions';
import MainPanelV2 from '../../components/prescriptionPad/rightPanel/workspace/MainPanelV2';
import BlockHistory from './BlockHistory';
import { setIsPageBlocked } from '../../App';
import { NavBar } from '../../components/prescriptionPad/rightPanel/workspace/NavBar';
import { LabVitalsRightPopUp } from '../../components/prescriptionPad/LabVitalsHistoricalTable';
import onContainerClick from './onClickContainer';
import { starKeyVital } from '../../store/actions/session';
import { getMyDbId } from '../../api/mactiveid';
import { queryToMap } from '../utils';
import { Patient } from '../../store/types/prescription';
import logEkaScribeOnCopyData from '../../api/logEkaScribeOnCopyData';
import { sanitizeRxTool } from '../../utils/commons';

type PPLocationState = {
    printConfig?: PrintConfig;
};

interface Props
    extends RouteComponentProps<PPRouterProps, StaticContext, PPLocationState | undefined> {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    summaryVisible: boolean;
    pastVitalsDialogVisible: boolean;
    patient?: Patient;
}

declare global {
    interface WindowEventMap {
        'ekascribe-data-v2': CustomEvent<{ value: string }>;
    }
}

interface onCopyDataFromV2RXReceivedInevent {
    sessionID: string;
}
class PrescriptionPadV2 extends PureComponent<Props, { keyVitals: string[] }> {
    collectOnCopyDataForAnalyticsEkaScribe = async (payload: onCopyDataFromV2RXReceivedInevent) => {
        const { sessionID } = payload || {};

        if (!this.props.match.params.pxId || !sessionID) {
            track('DATA_SENT_ON_COPY__BY_V2RX', {
                from: 'collectOnCopyDataForAnalyticsEkaScribe',
                status: 'INFO',
                prescriptionID: `precription id from our web-app —— ${this.props.match.params.pxId}`,
                sessionID: `session id recieved from eka-scribe extension —— ${sessionID}`,
            });
            return;
        }

        const result = await logEkaScribeOnCopyData({
            prescriptionID: this.props.match.params.pxId,
            sessionID,
        });
        if (!result?.success) {
            track('ERROR_COLLECTING_ONCOPY_DATA_FROM_V2RX', {
                from: 'collectOnCopyDataForAnalyticsEkaScribe',
                status: 'ERROR',
                message: result.message || '',
            });
        }
    };

    onCopyEventFromEkaScribe = (event: CustomEvent) => {
        const dataForAnlysticsPostCopyV2RX = JSON.parse(
            event.detail.value,
        ) as onCopyDataFromV2RXReceivedInevent;
        this.collectOnCopyDataForAnalyticsEkaScribe(dataForAnlysticsPostCopyV2RX);
    };
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    pxUpdate = (event) => {
        try {
            console.log('eventRxTool', event);
            const eventRxTool = JSON.parse(event?.detail?.value || '{}');
            console.log('eventRxTool', eventRxTool);
            const sanitisedRxTool = sanitizeRxTool(eventRxTool);
            this.props.dispatch(
                upsertPrescriptionV2({
                    ...sanitisedRxTool,
                }),
            );
        } catch (e) {
            console.error('Error updating prescription', e, event);
        }
    };

    componentDidMount = async (): Promise<void> => {
        setIsPageBlocked(true);
        getItemFromMisc<string[]>(getMyDbId() || '', MISC_KEYS.keyVitals)
            .then((keyVitals) => {
                this.setState({ keyVitals });
            })
            .catch((e) => console.error(e));
        await this.loadPx(this.props.match.params.pxId);

        window.addEventListener('ekascribe-data', this.pxUpdate);
        window.addEventListener('ekascribe-data-v2', this.onCopyEventFromEkaScribe);
    };

    componentDidUpdate = (prevProps: Props) => {
        if (prevProps.match.params.pxId !== this.props.match.params.pxId) {
            this.loadPx(this.props.match.params.pxId);
            setIsPageBlocked(true);
        }

        if (prevProps.match.params.subRoute !== this.props.match.params.subRoute) {
            this.props.dispatch(setToastType(undefined));
        }
    };

    loadPx = async (pxId?: string) => {
        if (!pxId) {
            console.log('pxId not passed');
            this.props.history.goBack();
            return;
        }

        timeTrackStart(PX_TIME);

        const filters = queryToMap(this.props.location.search);

        try {
            const [p, translation] = await Promise.all([
                getPrescriptionById(this.props.match.params.pxId),
                getItemFromMisc<Translate>(getMyDbId() || '', MISC_KEYS.translate),
            ]);

            if (p) {
                this.props.dispatch(loadPrescription(p, translation, filters));
                return;
            }

            // prescription not found
            this.props.history.replace('/');
        } catch (e) {
            console.error('PrescriptionPad: Error loading prescription', e);
        }
    };

    handleKeyboard = (): void => {
        this.props.dispatch(undo());
        return;
    };

    renderRightPanel = () => {
        const { match } = this.props;
        const { pxState } = match.params;

        switch (pxState) {
            case 'edit':
                return <MainPanelV2 subRoute={match.params.subRoute} />;
            case 'finish':
                return (
                    <div className="flex-1 overflow-scroll col-span-11 mt-2">
                        <FinishedPanel />
                    </div>
                );
            default:
                return <>Not sure</>;
        }
    };

    componentWillUnmount = (): void => {
        this.props.dispatch(clearPrescriptionPad());
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        track(PX_TIME, this.props.match.params);
        window.removeEventListener('ekascribe-data', this.pxUpdate);
        window.removeEventListener('ekascribe-data-v2', this.onCopyEventFromEkaScribe);
    };

    toggleKeyVital = async (vitalId: string): Promise<void> => {
        const currKeyVitals = this.state.keyVitals ?? [];

        let newKeyVitals = [];

        const doesExist = currKeyVitals.includes(vitalId);

        if (doesExist) {
            newKeyVitals = currKeyVitals.filter((v) => v !== vitalId);
        } else {
            newKeyVitals = [...currKeyVitals, vitalId];
        }

        this.setState({
            keyVitals: newKeyVitals,
            // sortKeyVitals: false,
        });

        track('UPDATE_STAR_VITALS', { from: 'vital-history-page-pad' });

        this.props.dispatch(starKeyVital(newKeyVitals));
    };

    render() {
        const { summaryVisible, pastVitalsDialogVisible, patient } = this.props;
        const patientId = patient?.localId || patient?.id || '';

        return (
            <Fragment>
                <BlockHistory />
                <Hotkeys keyName="command+z" onKeyDown={this.handleKeyboard} allowRepeat>
                    <Helmet>
                        <meta charSet="utf-8" />
                        <title>EKA - Prescription Pad</title>
                    </Helmet>

                    <div
                        onClick={onContainerClick}
                        className="min-w-840 flex flex-col h-full font-lato bg-darwin-neutral-75 padV2"
                    >
                        <NavBar />

                        {this.renderRightPanel()}
                        <div className="absolute bottom-80 z-1 min-w-560 left-1/2 transform -translate-x-1/2">
                            <AnimatePresence>
                                <DraftToast />
                                <PushSuccessToast />
                            </AnimatePresence>
                        </div>
                        <AnimatePresence>
                            {pastVitalsDialogVisible && (
                                <LabVitalsRightPopUp
                                    keyVitals={this.state.keyVitals}
                                    toggleKeyVitals={this.toggleKeyVital}
                                    patientId={patientId}
                                />
                            )}
                        </AnimatePresence>
                    </div>
                    <AnimatePresence>{summaryVisible && <SummaryView />}</AnimatePresence>
                </Hotkeys>
                <EditPatientWrapperDialog />
            </Fragment>
        );
    }
}

export default connect((state: GlobalState) => ({
    referalDialogVisible: isReferalDialogVisible(state),
    summaryVisible: isSummaryDialogVisible(state),
    pastVitalsDialogVisible: getIsPastVitalsDialogVisible(state),
    patient: getPatient(state),
}))(PrescriptionPadV2);
